import { inject, Injectable } from '@angular/core';
import { LocalStorageApiService } from '@nebalkon-workspace/browser-api';
import { LocalStorageMobileKeys } from '../../entities/enum/local-storage-mobile-keys.enum';

/** Элемент очереди редиректов в localStorage (JSON-массив). */
export interface RedirectQueueItem {
  url: string;
  action: string;
}

@Injectable({ providedIn: 'root' })
export class RedirectQueueService {
  readonly #storage = inject(LocalStorageApiService);

  getAll(): RedirectQueueItem[] {
    return this.#readRaw();
  }

  setAll(items: RedirectQueueItem[]): void {
    const normalized = items
      .map((r) => this.#normalize(r))
      .filter((r): r is RedirectQueueItem => r !== null);
    this.#write(normalized);
  }

  add(item: RedirectQueueItem): void {
    const nextItem = this.#normalize(item);
    if (!nextItem) {
      return;
    }
    const next = [...this.#readRaw(), nextItem];
    this.#write(next);
  }

  clear(): void {
    this.#storage.removeItem(LocalStorageMobileKeys.RedirectQueue);
  }

  #readRaw(): RedirectQueueItem[] {
    const raw = this.#storage.getItem(LocalStorageMobileKeys.RedirectQueue);
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) {
        return [];
      }
      const out: RedirectQueueItem[] = [];
      for (const el of parsed) {
        if (!el || typeof el !== 'object') {
          continue;
        }
        const rec = el as Record<string, unknown>;
        const urlRaw = rec['url'];
        const actionRaw = rec['action'];
        const url = typeof urlRaw === 'string' ? urlRaw.trim() : '';
        const action = typeof actionRaw === 'string' ? actionRaw.trim() : '';
        if (url && action) {
          out.push({ url, action });
        }
      }
      return out;
    } catch {
      return [];
    }
  }

  #write(items: RedirectQueueItem[]): void {
    if (items.length === 0) {
      this.clear();
      return;
    }
    this.#storage.setItem(
      LocalStorageMobileKeys.RedirectQueue,
      JSON.stringify(items),
    );
  }

  #normalize(r: RedirectQueueItem): RedirectQueueItem | null {
    const url = typeof r.url === 'string' ? r.url.trim() : '';
    const action = typeof r.action === 'string' ? r.action.trim() : '';
    if (!url || !action) {
      return null;
    }
    return { url, action };
  }
}
